<template>
  <v-container>
    <!-- Form to Add Premium QR Codes -->
    <v-form v-model="valid" @submit.prevent="addPremiumQRCodes" class="form-container">
      <!-- Dropdown to Select QR Code Name -->
      <v-select
        v-model="selectedQRCode"
        :items="qrCodeOptions"
        item-text="label"
        item-value="value"
        label="Sélectionner le nom d'abonnement"
        required
        outlined
        @change="checkQRCodeUsability"
        class="form-select"
      ></v-select>

      <!-- Input to Specify Number of Times to Add the QR Code -->
      <v-text-field
        v-model.number="numQRCodes"
        label="Quantité"
        type="number"
        min="1"
        required
        outlined
        class="form-text-field"
      ></v-text-field>

      <v-btn :disabled="!valid || qrUsability === false" type="submit" color="primary" class="form-button">
        Ajouter
      </v-btn>
    </v-form>

    <!-- Data Table to Display Premium QR Codes -->
    <v-data-table
      :headers="headers"
      :items="premiumQRCodes"
      item-value="id"
      class="elevation-1"
      :items-per-page="5"
      item-key="id"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
    >
      <!-- Column for Select All Checkbox -->
      <template v-slot:top>
        <v-checkbox
          v-model="selectAll"
          @change="toggleSelectAll"
          label="Tout sélectionner"
        ></v-checkbox>
      </template>

      <!-- Column for Select Checkbox -->
      <template v-slot:item.select="{ item }">
        <v-checkbox
          v-model="selectedQRCodeIds"
          :value="item.id"
          color="primary"
        ></v-checkbox>
      </template>

      <!-- Column for Is Used -->
      <template v-slot:item.isUsed="{ item }">
        <v-icon :class="item.isUsed ? 'unused-icon' : 'used-icon'">
          {{ item.isUsed ? 'mdi-close-circle' : 'mdi-check-circle' }}
        </v-icon>
      </template>

      <!-- Column for Delete Button -->
      <template v-slot:item.delete="{ item }">
        <v-btn @click="confirmDeleteQRCode(item.id)" color="red" icon class="table-btn">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Button to Generate Selected QR Codes -->
    <v-btn @click="generateSelectedQRCodes" color="primary" class="generate-button">
      Générer QR Codes
    </v-btn>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="showDeleteConfirmDialog" max-width="350">
      <v-card>
        <v-card-title class="headline">Confirmer la suppression</v-card-title>
        <v-card-text>Êtes-vous sûr de vouloir supprimer ce QR Code ?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="showDeleteConfirmDialog = false">
            Annuler
          </v-btn>
          <v-btn color="red darken-1" text @click="deleteQRCode">
            Supprimer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Loading Indicator -->
    <v-dialog v-model="loading" max-width="320">
      <v-card>
        <v-card-title class="headline">Téléchargement en cours...</v-card-title>
        <v-card-text>
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import jsPDF from 'jspdf';
import Constant from "@/utils/constants";

export default {
  data() {
    return {
      valid: false,
      selectedQRCode: null,
      numQRCodes: 1,
      qrUsability: null,
      qrCodeOptions: [],
      premiumQRCodes: [],
      selectedQRCodeIds: [], // Array to hold selected QR code IDs
      headers: [
        { text: 'Sélectionner', value: 'select', sortable: false },
        { text: 'Nom', value: 'name' },
        { text: 'Type', value: 'duration' },
        { text: 'Utilisabilité', value: 'isUsed' },
        { text: 'Supprimer', value: 'delete', sortable: false },
      ],
      showDeleteConfirmDialog: false,
      qrCodeToDelete: null,
      loading: false,
      selectAll: false, // Boolean for 'Select All' checkbox
      itemsPerPage: 5,
      page: 1,
    };
  },
  watch: {
    selectAll(newValue) {
      if (newValue) {
        this.selectedQRCodeIds = this.premiumQRCodes.map(qrCode => qrCode.id);
      } else {
        this.selectedQRCodeIds = [];
      }
    },
    premiumQRCodes(newItems) {
      // Ensure selected items persist when data changes
      const selected = this.selectedQRCodeIds;
      this.selectedQRCodeIds = newItems.filter(item => selected.includes(item.id)).map(item => item.id);
    },
  },
  methods: {
    async fetchQRCodes() {
      try {
        const response = await fetch(Constant.QORANI + 'premium/getall');
        const result = await response.json();
        if (response.ok) {
          this.qrCodeOptions = result.map((item) => ({
            label: item.name,
            value: item,
          }));
        } else {
          console.error('Error:', result.message);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    },
    checkQRCodeUsability() {
      const selected = this.selectedQRCode;
      if (selected) {
        this.qrUsability = !selected.isUsed;
      }
    },
    async addPremiumQRCodes() {
      const selected = this.selectedQRCode;
      if (!selected) {
        alert('Please select a QR code.');
        return;
      }
      if (this.numQRCodes < 1) {
        alert('The number of times to add must be at least 1.');
        return;
      }

      try {
        for (let i = 0; i < this.numQRCodes; i++) {
          const uniqueNumber = `${new Date().getTime()}-${Math.floor(Math.random() * 10000)}`;
          const qrCodeData = {
            id: selected.id,
            qrValue: `${selected.id}`,
            isUsed: selected.isUsed,
            nbItem: this.numQRCodes,
          };

          const response = await fetch(Constant.QORANI + 'premiumQRCode/add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(qrCodeData),
          });
          const result = await response.json();

          if (response.ok) {
            const newQRCodes = Array.from({ length: this.numQRCodes }, () => ({ ...qrCodeData }));
            this.premiumQRCodes.push(...newQRCodes);
            this.selectedQRCode = null;
            this.numQRCodes = 1;
            this.qrUsability = null;
          } else {
            console.error('Error:', result.message);
          }
        }
      } catch (error) {
        console.error('Error:', error);
      }
    },
    confirmDeleteQRCode(qrCodeId) {
      this.qrCodeToDelete = qrCodeId;
      this.showDeleteConfirmDialog = true;
    },
    async deleteQRCode() {
      try {
        const qrCodeId = this.qrCodeToDelete;
        const response = await fetch(Constant.QORANI + `premiumQRCode/delete/${qrCodeId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          this.premiumQRCodes = this.premiumQRCodes.filter((qrCode) => qrCode.id !== qrCodeId);
        } else {
          const result = await response.json();
          console.error('Error:', result.message);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        this.showDeleteConfirmDialog = false;
        this.qrCodeToDelete = null;
      }
    },
    async downloadQRCodeAsPDF(qrValues) {
      this.loading = true;
      const qrSize = 30;
      const xSpacing = 10;
      const ySpacing = 10;
      const rowsPerPage = 7;
      const colsPerPage = 5;
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const totalGridWidth = colsPerPage * qrSize + (colsPerPage - 1) * xSpacing;
      const totalGridHeight = rowsPerPage * qrSize + (rowsPerPage - 1) * ySpacing;
      const yOffset = (pageHeight - totalGridHeight) / 2 - 5; 
      const xOffset = (pageWidth - totalGridWidth) / 2;
      let qrIndex = 0;

      while (qrIndex < qrValues.length) {
        for (let row = 0; row < rowsPerPage; row++) {
          for (let col = 0; col < colsPerPage; col++) {
            if (qrIndex >= qrValues.length) break;
            const qrValue = qrValues[qrIndex];
            //const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${qrValue}&size=${qrSize}x${qrSize}`;
            const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${qrValue}&size=150x150`;

            const response = await fetch(qrCodeUrl);
            const blob = await response.blob();
            const imageURL = URL.createObjectURL(blob);
            const x = xOffset + col * (qrSize + xSpacing);
            const y = yOffset + row * (qrSize + ySpacing);
            doc.addImage(imageURL, 'PNG', x, y, qrSize, qrSize);
            qrIndex++;
          }
        }

        if (qrIndex < qrValues.length) {
          doc.addPage();
        }
      }

      doc.save('QRCodes.pdf');
      this.loading = false;
    },
    async generateSelectedQRCodes() {
      if (this.selectedQRCodeIds.length === 0) {
        alert('Please select at least one QR code.');
        return;
      }
      const selectedQRCodes = this.premiumQRCodes.filter(qrCode =>
        this.selectedQRCodeIds.includes(qrCode.id)
      );
      await this.downloadQRCodeAsPDF(selectedQRCodes.map(qrCode => qrCode.qrValue));
    },
    async getAllPremiumQRCodes() {
      try {
        const response = await fetch(Constant.QORANI + 'premiumQRCode/getall');
        const result = await response.json();
        if (response.ok) {
          this.premiumQRCodes = result;
        } else {
          console.error('Error:', result.message);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedQRCodeIds = this.premiumQRCodes.map(qrCode => qrCode.id);
      } else {
        this.selectedQRCodeIds = [];
      }
    }
  },
  mounted() {
    this.fetchQRCodes();
    this.getAllPremiumQRCodes();
  },
};
</script>
<style scoped>
/* Container for the form */
.form-container {
  max-width: 600px;
  margin: 50px auto;
  padding: 30px;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.15);
}

/* Styles for the form fields */
.form-select,
.form-text-field {
  margin-bottom: 20px;
}

/* Styles for the submit button */
.form-button {
  margin-top: 20px;
  width: 50%;
  margin-left: 130px;
  
}

/* Styles for the data table */
.v-data-table {
  margin-top: 30px;
}

/* Styles for the table header */
.v-data-table-header th {
  background-color: #f5f5f5;
  font-weight: bold;
  color: #333;
}

/* Styles for table rows */
.v-data-table .v-data-table__wrapper {
  background-color: #fafafa;
}

/* Styles for select checkbox in the table */
.v-data-table .v-checkbox {
  margin: 0;
}

/* Styles for action buttons in the table */
.table-btn {
  min-width: 100px;
}

/* Styles for used and unused QR code icons */
.used-icon {
  color: #4caf50; /* Green for used QR codes */
}

.unused-icon {
  color: #f44336; /* Red for unused QR codes */
}

/* Styles for the generate button */
.generate-button {
  margin-top: 20px;
  width: 50%;
  margin-left: 270px;
}

/* Styles for the confirmation dialog */
.v-dialog {
  .v-card {
    border-radius: 8px;
    box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.1);
  }

  .v-card-title {
    background-color: #2196f3;
    color: #ffffff;
    padding: 16px;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .v-card-text {
    padding: 16px;
    font-size: 1rem;
  }

  .v-card-actions {
    padding: 16px;
    border-top: 1px solid #e0e0e0;
  }

  .v-btn {
    font-weight: 100;
    letter-spacing: 0.5px;
  }
}
/* Styles for the loading indicator */
.v-dialog.loading-dialog {
  .v-card {
    border-radius: 8px;
    box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.1);
  }

  .v-card-title {
    background-color: #2196f3;
    color: #ffffff;
    padding: 16px;
    font-size: 1.25rem;
    font-weight: 600;
    text-align: center;
  }

  .v-card-text {
    padding: 16px;
    text-align: center;
  }
}
</style>
